/**
 * Charts for the Overview. Recharts, themed through the CSS variables so light
 * and dark come for free. Each chart takes plain rows and nothing else, and
 * each one carries its own key: three unlabelled coloured lines is a picture,
 * not a chart.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReactElement } from 'react';
import type { OverviewStats } from '../api/types';
import { cn } from '@/lib/utils';

const axisStyle = { fill: 'var(--ink-faint)', fontSize: 12 } as const;

/** The key. Sits above the plot so it reads before the shapes do. */
export function ChartLegend({
  items,
  className,
}: {
  items: Array<{ label: string; color: string }>;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1', className)}>
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5 text-meta text-ink-dim">
          <span className="size-2 rounded-full" style={{ background: i.color }} aria-hidden />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function TipBox({
  active,
  label,
  payload,
  suffix,
}: {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ name?: string; value?: number | string; color?: string }>;
  suffix?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-control border border-line bg-popover px-3 py-2 text-meta shadow-pop">
      <div className="mb-1 font-semibold text-ink">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5 text-ink-dim">
            <span className="size-2 rounded-full" style={{ background: p.color }} aria-hidden />
            {p.name}
          </span>
          <span className="font-semibold text-ink">
            {p.value}
            {suffix ?? ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function Frame({ height, children }: { height: number; children: ReactElement }) {
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer>{children}</ResponsiveContainer>
    </div>
  );
}

function dayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Sessions started, completed and failed per day over the window. */
export function SessionsPerDay({
  rows,
  days,
  height = 260,
}: {
  rows: OverviewStats['perDay'];
  days: number;
  height?: number;
}) {
  // Fill the window so quiet days show as zero rather than vanishing.
  const byDate = new Map(rows.map((r) => [r.date, r]));
  const data: Array<{ day: string; Started: number; Completed: number; Failed: number }> = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const r = byDate.get(key);
    data.push({
      day: dayLabel(key),
      Started: r?.started ?? 0,
      Completed: r?.completed ?? 0,
      Failed: r?.failed ?? 0,
    });
  }
  return (
    <Frame height={height}>
      <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
        <XAxis
          dataKey="day"
          tick={axisStyle}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
          minTickGap={28}
        />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} width={44} />
        <Tooltip content={<TipBox />} cursor={{ stroke: 'var(--line-strong)' }} />
        <Line
          type="monotone"
          dataKey="Started"
          stroke="var(--chart-1)"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          type="monotone"
          dataKey="Completed"
          stroke="var(--chart-2)"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          type="monotone"
          dataKey="Failed"
          stroke="var(--chart-3)"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </Frame>
  );
}

/**
 * First-pass rate per step: where people retake photos.
 *
 * Every step keeps a faint full-height track behind its bar, so a step nobody
 * has reached yet reads as "no data" rather than as a zero score. A bar drawn
 * to nothing and a bar that is genuinely nothing look identical otherwise,
 * which is the worst thing this chart could say.
 */
export function StepFirstPass({
  rows,
  height = 260,
}: {
  rows: OverviewStats['perStep'];
  height?: number;
}) {
  const data = rows.map((r) => ({
    step: `${r.stepNumber}`,
    title: r.title,
    hasData: r.firstPassRate !== null,
    'First pass': r.firstPassRate === null ? 0 : Math.round(r.firstPassRate * 100),
    Retakes: r.retries,
  }));
  return (
    <Frame height={height}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%">
        <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
        <XAxis dataKey="step" tick={axisStyle} axisLine={false} tickLine={false} />
        <YAxis
          tick={axisStyle}
          axisLine={false}
          tickLine={false}
          domain={[0, 100]}
          width={44}
          tickFormatter={(v: number) => `${v}%`}
        />
        <Tooltip
          content={<TipBox suffix="%" />}
          cursor={{ fill: 'var(--muted-surface)', opacity: 0.5 }}
          labelFormatter={(label: string | number) => {
            const row = data.find((d) => d.step === String(label));
            if (!row) return `Step ${label}`;
            return row.hasData
              ? `Step ${row.step}: ${row.title}`
              : `Step ${row.step}: ${row.title} (not reached yet)`;
          }}
        />
        <Bar
          dataKey="First pass"
          radius={[5, 5, 0, 0]}
          background={{ fill: 'var(--surface-sunken)', radius: 5 }}
        >
          {data.map((d) => (
            <Cell
              key={d.step}
              fill={
                !d.hasData
                  ? 'transparent'
                  : d['First pass'] >= 90
                    ? 'var(--chart-2)'
                    : d['First pass'] >= 70
                      ? 'var(--chart-3)'
                      : 'var(--error)'
              }
            />
          ))}
        </Bar>
      </BarChart>
    </Frame>
  );
}

/**
 * Sessions per piece of equipment. The height follows the row count: a fixed
 * frame either crushes eight bars or leaves one floating in an empty box.
 */
export function EquipmentBars({ rows }: { rows: OverviewStats['equipment'] }) {
  const data = rows.slice(0, 8).map((r) => ({
    name: r.name.length > 30 ? `${r.name.slice(0, 29)}…` : r.name,
    Sessions: r.sessions,
    Completed: r.completed,
  }));
  const height = Math.max(140, data.length * 46 + 36);
  return (
    <Frame height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
        barCategoryGap="26%"
        barGap={2}
      >
        <CartesianGrid stroke="var(--chart-grid)" horizontal={false} />
        <XAxis
          type="number"
          tick={axisStyle}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={200}
          tick={axisStyle}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<TipBox />} cursor={{ fill: 'var(--muted-surface)', opacity: 0.5 }} />
        <Bar dataKey="Sessions" fill="var(--chart-1)" radius={[0, 4, 4, 0]} barSize={12} />
        <Bar dataKey="Completed" fill="var(--chart-2)" radius={[0, 4, 4, 0]} barSize={12} />
      </BarChart>
    </Frame>
  );
}
